<template>
<!-- WHAT: A components to collect dims-metrics model data (Copied from BtMetricsModel).
   HISTORY:
   * V260318.1: Added an arrayRequired rule to dims and ensured metrics are displayed only when the first dimension is selected.
   * V260128.2: Added date() version of DATEs to dimColumns as well (before, it was only for TIMESTAMPs).
   * V260127.1: Added app_code and event_code to dimColumns when missing (in some schemas they may be BIGINT).
   * V260105.1: [not sent to RF] Removed hasFilter prop as it wasn't in use.
   * V251027.2: Supported 2-way binding: Initialized form fields from v-model.
   * V250808.1: Expanded dimColumns by adding date() version of timestamp schemas fields as well.
   * V250807.2: Added hasFilter prop and passed it to bt-metrics-model + Expanded dimColumns by adding date/timestamp schemas fields as well.
   * V250708.1: Modified v-col sizes to always fill up the row.
   * V250707.1: Added counter prop to the dims autocomplete.
   * V250702.1: In formChanged(), added all the selected dimensions to orderBy + Removed max prop.
   * V250701.1: 1st release.
-->
<v-form lazy-validation
   ref="form"
   v-model="isFormValid"
>
   <v-row class="px-2 pb-2">
      <v-col xs="12" sm="12" md="6" class="py-0"
      >
            <!--V260318 :error-messages="dimsErrMsg" -->
         <v-autocomplete multiple persistent-hint required deletable-chips small-chips
            ref="dims"
            autocomplete="off"
            class="py-0"
            placeholder="select one or more dimensions..."
            hint="* Dimensions"
            :counter="dimColumns.length"
            :items="dimColumns"
            :rules="[rules.arrayRequired]"
            v-model="dims"
            @change="dimsChanged"
         ></v-autocomplete>
      </v-col>
      <v-col xs="12" sm="12" md="6" class="pt-1 pb-0">
         <v-select dense persistent-hint hide-selected required
            ref="orderBy"
            autocomplete="off"
            hint="* Order by"
            :items="orderByItems"
            v-model="orderBy"
            @change="formChanged"
         ></v-select>
      </v-col>
   </v-row>
   <v-row v-if="dims.length"><!-- V260318: Added v-if -->
      <v-col cols="12" class="pb-0">
         <bt-metrics-model has-filter
            :initial-model="initialModel"
            :schemas="schemas"
            :query-submitted="querySubmitted"
            v-model="metricsData"
            @form-valid="metricsDataChanged"
         />
      </v-col>
   </v-row>
</v-form>
</template>

<script>
import BtMetricsModel from './BtMetricsModel.vue';
import { isEmpty } from '../mixins/bt-mixin.js';

const NAME = "BtDimsMetricsModel";
const VERSION = 'V260318.1';

export default {
   name: NAME,

   components: {
      BtMetricsModel
   },

   props: {
      value: {
         type: Object,
         default: () => ({})
      },
      debug: {
         type: Boolean,
         default: false
      },
      // hasFilter: {
      //    type: Boolean,
      //    default: false
      // },
      initialModel: {
         type: Boolean
         // default: true
      },
      schemas: {
         type: Array,
         required: true,
         default: () => []
      },
      querySubmitted: {
         type: Boolean,
         required: true
      }
   },

   data() {
      return {
         rules: {
            arrayRequired: value => value.length > 0 || "Value is required!"
         },
         orderByItems: [
            { text: 'Ascending', value: 'asc' },
            { text: 'Descending', value: 'desc' }
         ],
         isFormValid: false,
         isMetricsModelValid: true,
         // isFormChanged: false,
         dims: [],
         dimsErrMsg: '',
         orderBy: 'asc',
         metricsData: {},
      }
   },

   computed: { },

   watch: {
      initialModel: {
         immediate: true,
         handler(val) {
            if (val) this.init();
         }
      },

      schemas: {
         immediate: true,
         deep: true,
         async handler(val) {
            // this.alert('in ' + NAME + '/watch: schemas=' + JSON.stringify(val));
            // this.dims = [];
            // this.orderBy = 'asc';
            // this.formChanged();
            if (val?.length) {
               //V250807.2: this.dimColumns = val.filter(col => col.column_type === 'VARCHAR')
               //    .map(col => col.column_name);
               this.dimColumns = [
                  ...val
                     .filter(col => col.column_type === 'VARCHAR' || ['app_code', 'event_code'].includes(col.column_name)) //V260127
                     .map(col => ({
                        text: col.column_name,
                        value: col.column_name
                     })),
                  ...val
                     .filter(col => col.column_type === 'DATE' || col.column_type === 'TIMESTAMP')
                     .flatMap(col => {
                        const items = [
                           // V260128.2
                           {
                              text: `date(${col.column_name})`,
                              value: `STRFTIME(CAST(${col.column_name} as DATE), '%Y-%m-%d') as ${col.column_name}_date`
                           },
                           {
                              text: `day(${col.column_name})`,
                              value: `day(${col.column_name}) as ${col.column_name}_day`
                           },
                           {
                              text: `month(${col.column_name})`,
                              value: `month(${col.column_name}) as ${col.column_name}_month`
                           }
                        ];
                        // V250808.1 & V260128.2
                        // if (col.column_type === 'TIMESTAMP') {
                        //    items.unshift({
                        //       text: `date(${col.column_name})`,
                        //       value: `STRFTIME(CAST(${col.column_name} as DATE), '%Y-%m-%d') as ${col.column_name}_date`
                        //    });
                        // }

                        return items;
                     })
               ];
            } else {
               this.dimColumns = [];
            }

            this.init();

            this.$nextTick(() => {
               // this.formChanged();
               this.$refs.dims.focus();
            });
         }
      }
   },

   methods: {
      alert(msg) {
         if (this.debug) {
            alert(`${NAME} ${VERSION} says:\n${msg}`);
         }
      },

      log(msg, always =false) {
         if (this.debug || always) {
            console.log(`-----${NAME} ${VERSION} says => ${msg}`);
         }
      },

      init() {
         if (isEmpty(this.value) || !this.dimColumns?.length) {
            this.dims = [];
            this.orderBy = 'asc';
            this.metricsData = {};
            this.isMetricsModelValid = false;
         } else {
            const { dims, orderBy, metrics, whereClause } = this.value;
            this.dims = dims.filter(dim =>
               this.dimColumns.some(col => col.value === dim)
            );
            const orderByParts = orderBy.split(',')[0].split(' ');
            this.orderBy = orderByParts[orderByParts.length - 1];
            this.metricsData = {
               metrics: [...metrics],
               ...(whereClause && { whereClause: whereClause })
            }
            this.isMetricsModelValid = true;
         }
         // console.warn('value='+JSON.stringify(this.value));
         // console.warn('column='+this.column);
         // this.alert('init');
      },

      dimsChanged(val) {
         this.dimsErrMsg = val.length ? '' : 'Value is required!';
         this.formChanged();
      },

      metricsDataChanged(val) {
         // alert('in metricsDataChanged(): val=' + val);
         this.isMetricsModelValid = val;
         this.formChanged();
      },

      formChanged() {
         let emitVal = false;
         this.isFormValid = this.$refs.form?.validate();
         // alert('in formChanged(): isFormValid=' + this.isFormValid);
         // if (this.isFormValid && this.isMetricsModelValid) {
         if (!this.dimsErrMsg && this.isMetricsModelValid) {
            const orderBy = [];
            this.dims.forEach(item => {
               //V250807.2: orderBy.push(`${item} ${this.orderBy}`);
               //V250808.1: const alias = item.includes(' as ') ? item.split(' as ')[1] : item;
               const parts = item.split(' as ');
               const alias = parts[parts.length - 1];
               orderBy.push(`${alias} ${this.orderBy}`);
            });

            const modelData = {
               dims: this.dims,
               ...this.metricsData,
               orderBy: orderBy.join(',')
            };

            this.$emit('input', modelData);
            emitVal = true;
         }

         this.$emit('form-valid', emitVal);
      }
   },

   mounted() {
      // alert(`in ${NAME}.mounted(): `);
      if (this.initialModel === undefined)
         this.init();
   }
}
</script>

<style scoped>
</style>